import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Plus, Trash, CaretDown, CaretRight } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../lib/api';
import { useOwnerFetch, OwnerUnlockPrompt } from '../lib/ownerFetch';
import GolfApiImport from '../components/GolfApiImport';

const DEFAULT_HP = [4, 4, 3, 5, 4, 3, 4, 5, 4, 4, 4, 3, 5, 4, 3, 4, 5, 4];
const DEFAULT_SI = [1, 3, 15, 7, 5, 17, 9, 11, 13, 2, 4, 16, 8, 6, 18, 10, 12, 14];

function makeTee(copyFrom) {
  return {
    name: '', gender: 'Men', course_rating: 72.0, slope_rating: 113,
    hole_pars: [...(copyFrom?.hole_pars || DEFAULT_HP)],
    stroke_indexes: [...(copyFrom?.stroke_indexes || DEFAULT_SI)],
  };
}

function TeeHoleGrid({ tee, teeIdx, onUpdate, teeErrors }) {
  const hp = tee.hole_pars || DEFAULT_HP;
  const si = tee.stroke_indexes || DEFAULT_SI;
  const outTotal = hp.slice(0, 9).reduce((a, b) => a + b, 0);
  const inTotal = hp.slice(9).reduce((a, b) => a + b, 0);

  const setHP = (holeIdx, val) => {
    const a = [...hp]; a[holeIdx] = parseInt(val) || 0;
    onUpdate(teeIdx, 'hole_pars', a);
  };
  const setSI = (holeIdx, val) => {
    const a = [...si]; a[holeIdx] = parseInt(val) || 0;
    onUpdate(teeIdx, 'stroke_indexes', a);
  };

  return (
    <div className="mt-2">
      <div className="grid grid-cols-[auto_1fr_1fr] gap-x-3 gap-y-1 text-xs">
        <span className="font-semibold text-[#455561]">Hole</span>
        <span className="font-semibold text-[#455561]">Par</span>
        <span className="font-semibold text-[#455561]">SI</span>
        {hp.map((p, i) => {
          const siDupe = !!(teeErrors || {})[`${teeIdx}-si-dupe-${i}`];
          return (
            <div key={i} className="contents">
              <span className={`font-bold py-1 ${i === 9 ? 'border-t border-[#E1E6E4] pt-2' : ''} text-[#1D242B]`}>{i + 1}</span>
              <Input type="number" min={3} max={6} value={p} onChange={e => setHP(i, e.target.value)}
                className={`h-8 text-center text-sm ${i === 9 ? 'border-t border-[#E1E6E4] mt-1' : ''}`}
                data-testid={`tee-${teeIdx}-hp-${i}`} />
              <Input type="number" min={1} max={18} value={si[i]} onChange={e => setSI(i, e.target.value)}
                className={`h-8 text-center text-sm ${i === 9 ? 'border-t border-[#E1E6E4] mt-1' : ''} ${siDupe ? 'border-[#D32F2F] ring-1 ring-[#D32F2F]' : ''}`}
                data-testid={`tee-${teeIdx}-si-${i}`} />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-2 text-xs font-semibold text-[#455561] border-t border-[#E1E6E4] pt-1.5">
        <span>Out: {outTotal}</span><span>In: {inTotal}</span><span>Total: {outTotal + inTotal}</span>
      </div>
    </div>
  );
}

export default function OwnerAddCourse() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const editOid = params.get('oid');
  const editId = editOid || params.get('edit'); // oid preferred; slug fallback for old bookmarks
  const fromRequestId = params.get('from_request');
  const prefillName = params.get('prefill_name');

  const [name, setName] = useState(prefillName || '');
  const [region, setRegion] = useState('');
  const [tees, setTees] = useState([makeTee()]);
  const [expanded, setExpanded] = useState({ 0: true });
  const [teeErrors, setTeeErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [importWarnings, setImportWarnings] = useState([]);
  const [importApiCourseId, setImportApiCourseId] = useState(null);
  const [loaded, setLoaded] = useState(!editId); // true for new course, false until edit data hydrates
  const [courseOid, setCourseOid] = useState(null); // Mongo _id for delete
  const { ownerFetch, unlockProps } = useOwnerFetch();

  // Apply imported course data (from Golf API import via nav state or onImport callback)
  const applyImport = useCallback((course) => {
    if (!course) return;
    setName(course.name || '');
    setRegion(course.region || '');
    if (course.api_course_id) setImportApiCourseId(course.api_course_id);
    if (course.tees?.length > 0) {
      setTees(course.tees.map(t => ({
        name: t.name || '', gender: t.gender || 'Men',
        course_rating: t.course_rating ?? 72.0,
        slope_rating: t.slope_rating ?? 113,
        hole_pars: t.hole_pars ? [...t.hole_pars] : [...DEFAULT_HP],
        stroke_indexes: t.stroke_indexes ? [...t.stroke_indexes] : [...DEFAULT_SI],
      })));
      setExpanded({ 0: true });
    }
    setImportWarnings(course.warnings || []);
  }, []);

  // On mount: check for importedCourse from OwnerCoursesList navigation
  useEffect(() => {
    if (location.state?.importedCourse) {
      applyImport(location.state.importedCourse);
      // Clear the state so refreshing doesn't re-apply
      window.history.replaceState({}, document.title);
    }
  }, [location.state, applyImport]);

  const [loadError, setLoadError] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const loadCourse = useCallback(async () => {
    if (!editId) return;
    setLoadError(false);
    setSessionExpired(false);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      let c = null;
      if (editOid) {
        // Load by _id via dedicated endpoint (no encoding issues)
        const { data } = await api.get(`/owner/courses/${editOid}`, { signal: controller.signal });
        c = data;
      } else {
        // Fallback: load all courses and find by slug (old ?edit= bookmarks)
        const { data } = await api.get('/courses', { signal: controller.signal });
        c = (data || []).find(x => x.id === editId);
      }
      clearTimeout(timer);
      if (c && c.tees?.length > 0 && c.name) {
        setName(c.name || '');
        setRegion(c.region || '');
        setTees(c.tees.map(t => ({
          name: t.name || '', gender: t.gender || 'Men',
          course_rating: t.course_rating, slope_rating: t.slope_rating,
          hole_pars: t.hole_pars ? [...t.hole_pars] : [...DEFAULT_HP],
          stroke_indexes: t.stroke_indexes ? [...t.stroke_indexes] : [...DEFAULT_SI],
        })));
        setExpanded({ 0: true });
        if (c._id) setCourseOid(c._id);
        setLoaded(true);
      } else {
        setLoadError(true);
      }
    } catch (err) {
      clearTimeout(timer);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if ((status === 401 || status === 403) && detail === 'owner_required') {
        setSessionExpired(true);
      } else {
        setLoadError(true);
      }
    }
  }, [editId, editOid]);

  useEffect(() => { loadCourse(); }, [loadCourse]);

  const updateTee = useCallback((idx, field, val) => {
    setTees(prev => {
      const a = [...prev];
      a[idx] = { ...a[idx], [field]: val };
      return a;
    });
    setTeeErrors(prev => { const e = { ...prev }; delete e[`${idx}-${field}`]; return e; });
  }, []);

  const addTee = () => {
    const copyFrom = tees[0]; // copy hole data from first tee
    setTees(prev => [...prev, makeTee(copyFrom)]);
  };
  const removeTee = (idx) => {
    setTees(prev => prev.filter((_, i) => i !== idx));
    setExpanded(prev => { const e = { ...prev }; delete e[idx]; return e; });
  };
  const toggleExpand = (idx) => setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }));

  const validateTees = () => {
    const errs = {};
    tees.forEach((t, i) => {
      if (!t.name.trim()) errs[`${i}-name`] = 'Required';
      if (t.course_rating < 50 || t.course_rating > 90) errs[`${i}-course_rating`] = '50–90';
      if (t.slope_rating < 55 || t.slope_rating > 155) errs[`${i}-slope_rating`] = '55–155';
      const hp = t.hole_pars || [];
      if (hp.length !== 18) errs[`${i}-hole_pars`] = '18 holes required';
      else if (hp.some(p => p < 3 || p > 6)) errs[`${i}-hole_pars`] = 'Par must be 3–6';
      const si = t.stroke_indexes || [];
      if (si.length !== 18) errs[`${i}-stroke_indexes`] = '18 SI required';
      else {
        // Find duplicates
        const seen = {};
        const dupes = new Set();
        si.forEach((v, j) => {
          if (seen[v] !== undefined) { dupes.add(seen[v]); dupes.add(j); }
          seen[v] = j;
        });
        if ([...si].sort((a, b) => a - b).join(',') !== Array.from({length: 18}, (_, j) => j + 1).join(',')) {
          errs[`${i}-stroke_indexes`] = 'Use each stroke index 1 to 18 once';
          dupes.forEach(j => { errs[`${i}-si-dupe-${j}`] = true; });
        }
      }
    });
    setTeeErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    setError('');
    if (!name.trim()) { setError('Course name is required'); return; }
    if (tees.length < 1) { setError('At least one tee is required'); return; }
    if (!validateTees()) return;
    setSaving(true);
    const payload = {
      name: name.trim(), region: region.trim() || null,
      tees: tees.map(t => ({
        name: t.name.trim(), gender: t.gender,
        course_rating: parseFloat(t.course_rating) || 72.0,
        slope_rating: parseInt(t.slope_rating) || 113,
        hole_pars: t.hole_pars, stroke_indexes: t.stroke_indexes,
      })),
      ...(fromRequestId && !editId ? { resolves_request_id: fromRequestId } : {}),
      ...(importApiCourseId && !editId ? { source: 'golf_api', source_id: importApiCourseId } : {}),
    };
    const requestFn = editId
      ? () => api.put(`/owner/courses/${editId}`, payload)
      : () => api.post('/owner/courses', payload);
    const result = await ownerFetch(requestFn);
    if (result.ok) {
      toast.success(editId ? 'Course updated' : (fromRequestId ? 'Course added — request resolved' : 'Course added'));
      navigate(-1);
    } else if (result.error) {
      setError(result.error);
    }
    setSaving(false);
  };


  return (
    <div className="min-h-screen bg-[#F4F6F5] pb-safe" data-testid="add-course-page">
      <div className="bg-[#1D242B] px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-white p-1"><ArrowLeft className="w-5 h-5" weight="bold" /></button>
        <h1 className="text-lg text-white font-bold">{editId ? 'Edit Course' : 'Add Course'}</h1>
      </div>
      <div className="px-4 py-6 max-w-lg mx-auto space-y-4">
        {/* Golf API Import — only shown when adding a new course (not editing) */}
        {!editId && (
          <>
            <GolfApiImport
              onImport={applyImport}
              onLocalEdit={(oid) => navigate(`/admin/add-course?oid=${oid}`)}
              initialQuery={prefillName || ''}
            />
            {importWarnings.length > 0 && (
              <Card className="p-3 bg-[#FFF8E1] border-[#FFD54F] rounded-xl" data-testid="import-warnings">
                <p className="text-xs font-semibold text-[#F57F17] mb-1">Import warnings</p>
                {importWarnings.map((w, i) => <p key={i} className="text-[10px] text-[#795548]">• {w}</p>)}
              </Card>
            )}
          </>
        )}
        <Card className="p-5 bg-white border-[#E1E6E4] rounded-xl space-y-4">
          <div>
            <label className="text-xs font-semibold text-[#455561] mb-1 block">Course Name *</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Royal Portrush (Dunluce)" data-testid="course-name-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#455561] mb-1 block">Location / Club</label>
            <Input value={region} onChange={e => setRegion(e.target.value)} placeholder="e.g. Northern Ireland" data-testid="course-region-input" />
          </div>
        </Card>

        {/* Tees — each with its own hole grid, collapsible */}
        {tees.map((t, i) => {
          const isOpen = !!expanded[i];
          const hp = t.hole_pars || DEFAULT_HP;
          const teePar = hp.reduce((a, b) => a + b, 0);
          const hasErr = Object.keys(teeErrors).some(k => k.startsWith(`${i}-`));
          return (
            <Card key={i} className={`bg-white border-[#E1E6E4] rounded-xl overflow-hidden ${hasErr ? 'border-[#D32F2F]' : ''}`} data-testid={`tee-row-${i}`}>
              {/* Header — always visible */}
              <button onClick={() => toggleExpand(i)} className="w-full flex items-center justify-between p-4 text-left hover:bg-[#F4F6F5] transition-colors" data-testid={`tee-toggle-${i}`}>
                <div className="flex items-center gap-2 min-w-0">
                  {isOpen ? <CaretDown className="w-4 h-4 text-[#455561] flex-shrink-0" /> : <CaretRight className="w-4 h-4 text-[#455561] flex-shrink-0" />}
                  <span className="text-sm font-bold text-[#1D242B] truncate">{t.name || `Tee ${i + 1}`}</span>
                  <span className="text-xs text-[#455561]">{t.gender} · Par {teePar} · CR {t.course_rating} · S {t.slope_rating}</span>
                </div>
                {tees.length > 1 && (
                  <button onClick={e => { e.stopPropagation(); removeTee(i); }} className="text-[#D32F2F] p-1 hover:bg-red-50 rounded ml-2 flex-shrink-0" data-testid={`tee-remove-${i}`}>
                    <Trash className="w-4 h-4" weight="bold" />
                  </button>
                )}
              </button>

              {/* Expanded body */}
              {isOpen && (
                <div className="px-4 pb-4 space-y-3 border-t border-[#E1E6E4]">
                  <div className="grid grid-cols-2 gap-2 pt-3">
                    <div>
                      <label className="text-[10px] text-[#455561] mb-0.5 block">Name *</label>
                      <Input value={t.name} onChange={e => updateTee(i, 'name', e.target.value)}
                        placeholder="e.g. White (Men)"
                        className={`h-9 text-sm ${teeErrors[`${i}-name`] ? 'border-[#D32F2F]' : ''}`}
                        data-testid={`tee-name-${i}`} />
                      {teeErrors[`${i}-name`] && <p className="text-[10px] text-[#D32F2F] mt-0.5">{teeErrors[`${i}-name`]}</p>}
                    </div>
                    <div>
                      <label className="text-[10px] text-[#455561] mb-0.5 block">Gender</label>
                      <select value={t.gender} onChange={e => updateTee(i, 'gender', e.target.value)}
                        className="w-full h-9 px-2 rounded-md border border-[#E1E6E4] text-sm bg-white"
                        data-testid={`tee-gender-${i}`}>
                        <option value="Men">Men</option>
                        <option value="Women">Women</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-[#455561] mb-0.5 block">Course Rating (50–90)</label>
                      <Input type="number" step="0.1" min={50} max={90} value={t.course_rating}
                        onChange={e => updateTee(i, 'course_rating', e.target.value)}
                        className={`h-9 text-sm ${teeErrors[`${i}-course_rating`] ? 'border-[#D32F2F]' : ''}`}
                        data-testid={`tee-cr-${i}`} />
                    </div>
                    <div>
                      <label className="text-[10px] text-[#455561] mb-0.5 block">Slope Rating (55–155)</label>
                      <Input type="number" min={55} max={155} value={t.slope_rating}
                        onChange={e => updateTee(i, 'slope_rating', parseInt(e.target.value) || 0)}
                        className={`h-9 text-sm ${teeErrors[`${i}-slope_rating`] ? 'border-[#D32F2F]' : ''}`}
                        data-testid={`tee-sr-${i}`} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-[#1D242B]">Par: {teePar}</span>
                    {(teeErrors[`${i}-hole_pars`] || teeErrors[`${i}-stroke_indexes`]) && (
                      <p className="text-[10px] text-[#D32F2F]">{teeErrors[`${i}-hole_pars`] || teeErrors[`${i}-stroke_indexes`]}</p>
                    )}
                  </div>
                  <TeeHoleGrid tee={t} teeIdx={i} onUpdate={updateTee} teeErrors={teeErrors} />
                </div>
              )}
            </Card>
          );
        })}

        <Button variant="outline" onClick={addTee} className="w-full h-10 rounded-xl text-xs border-dashed border-[#B8C7C0] text-[#455561]" data-testid="add-tee-btn">
          <Plus className="w-4 h-4 mr-1" weight="bold" /> Add Tee
        </Button>

        {error && <p className="text-sm text-[#D32F2F] text-center" data-testid="course-save-error">{error}</p>}

        {editId && !loaded && !loadError && !sessionExpired && (
          <p className="text-sm text-[#455561] text-center" data-testid="course-loading-msg">Loading course data…</p>
        )}

        {sessionExpired && (
          <div className="text-center space-y-2" data-testid="course-session-expired">
            <p className="text-sm text-[#D32F2F]">Please log in again</p>
            <p className="text-xs text-[#455561]">Your session has expired.</p>
          </div>
        )}

        {loadError && (
          <div className="text-center space-y-2" data-testid="course-load-error">
            <p className="text-sm text-[#D32F2F]">Couldn't load this course</p>
            <Button variant="outline" size="sm" onClick={loadCourse}
              className="text-xs border-[#E1E6E4]" data-testid="course-retry-btn">
              Retry
            </Button>
          </div>
        )}

        <OwnerUnlockPrompt {...unlockProps} />

        <Button onClick={handleSave} disabled={saving || deleting || !loaded || unlockProps.ownerExpired} className="w-full h-12 rounded-xl bg-[#1B4332] text-white font-semibold" data-testid="course-save-btn">
          {saving ? 'Saving...' : editId ? 'Save Changes' : 'Add Course'}
        </Button>

        {editId && !!sessionStorage.getItem('foretour_platform_token') && courseOid && (
          <Button variant="outline" onClick={async () => {
            if (!window.confirm('Are you sure you want to delete this course? This cannot be undone.')) return;
            setDeleting(true);
            setError('');
            const result = await ownerFetch(() => api.delete(`/owner/courses/${courseOid}`));
            if (result.ok) {
              toast.success('Course deleted');
              navigate(-1);
            } else if (result.error) {
              setError(result.error);
            }
            setDeleting(false);
          }}
            disabled={saving || deleting || unlockProps.ownerExpired}
            className="w-full h-10 rounded-xl border-[#D32F2F] text-[#D32F2F] hover:bg-red-50 text-xs font-semibold"
            data-testid="course-delete-btn">
            <Trash className="w-4 h-4 mr-1" weight="bold" />
            {deleting ? 'Deleting...' : 'Delete Course'}
          </Button>
        )}
      </div>
    </div>
  );
}
